import { Chip, Box } from "@mui/material";
import PropTypes from "prop-types";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import React from "react";

/**
 * Status badge component for claim statuses
 * Uses semantic color tokens from the theme
 *
 * @param {Object} props
 * @param {string} props.status - The status value (e.g., 'saved', 'pre_check', 'processing')
 * @param {string} props.label - The display label for the status
 * @param {string} props.size - Size of the chip ('small' | 'medium')
 * @param {function} [props.onClick] - Optional click handler for the badge
 * @param {boolean} [props.open] - Whether the badge is in an "open" state (for dropdown indicators)
 * @param {boolean} [props.disabled] - Whether the badge is disabled
 * @returns {JSX.Element}
 */
const ClaimStatusBadge = ({
  status,
  label,
  size = "small",
  onClick,
  open = false,
  disabled = false,
                            ariaLabelledby,
}) => {
  // Map backend status values to chip variant names
  const statusVariantMap = {
    saved: "claim-status-gespeichert",
    draft: "claim-status-entwurf",
    pre_check: "claim-status-pruefung",
    correction_customer: "claim-status-korrektur",
    correction_insurer: "claim-status-korrektur",
    processing: "claim-status-bearbeitung",
    released: "claim-status-bearbeitung",
    settled: "claim-status-abgeschlossen",
    partial_settled: "claim-status-teilreguliert",
    closed: "claim-status-abgeschlossen",
    closed_below_threshold: "claim-status-abgeschlossen",
    rejected: "claim-status-abgelehnt",
  };

  const variant = statusVariantMap[status] || "claim-status-gespeichert";

  // Custom styling for tiny size
  const tinySx =
    size === "tiny"
      ? {
          fontSize: "0.7rem",
          height: 20,
        }
      : {};

  return (
    <Chip
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span aria-labelledby={ariaLabelledby}>{label}</span>
          {onClick &&
            !disabled &&
            (open ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            ))}
        </Box>
      }
      variant={variant}
      size={size === "tiny" ? "small" : size}
      sx={tinySx}
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
    />
  );
};

ClaimStatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["tiny", "small", "medium"]),
  onClick: PropTypes.func,
  open: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default ClaimStatusBadge;
